/**
 * Notification vocabulary. Three levels, all closed unions so the UI, the server and the
 * delivery renderers cannot drift:
 *
 *   kind      — the specific thing that happened ("deploy.failed")
 *   category  — the row a person sees in their preferences ("Deployments")
 *   severity  — how loudly to say it, and whether a silence applies
 *
 * Kinds are derived from the state vocabulary in ./states.ts, not invented: every terminal
 * job maps through `kindForJob`, which is exhaustive over JOB_KINDS so adding a job kind
 * without deciding whether it notifies fails the build.
 */

import type { JobKind } from './states.js';

export const NOTIFICATION_SEVERITIES = ['info', 'warning', 'critical'] as const;
export type NotificationSeverity = (typeof NOTIFICATION_SEVERITIES)[number];

/** Per-user delivery channels. Project webhooks are configured per project, not per user. */
export const NOTIFICATION_CHANNELS = ['inapp', 'push', 'rocketchat'] as const;
export type NotificationChannel = (typeof NOTIFICATION_CHANNELS)[number];
export type ChannelSet = Record<NotificationChannel, boolean>;

/** The four groups the preferences screen is divided into. */
export const NOTIFICATION_GROUPS = ['delivery', 'infrastructure', 'data', 'security'] as const;
export type NotificationGroup = (typeof NOTIFICATION_GROUPS)[number];

export const NOTIFICATION_CATEGORIES = [
  'deploy',
  'operations',
  'approval',
  'health',
  'agent',
  'routing',
  'config',
  'capacity',
  'backup',
  'restore',
  'migration',
  'access',
  'shell',
  'secret',
  'platform',
] as const;
export type NotificationCategory = (typeof NOTIFICATION_CATEGORIES)[number];

/**
 * Who a category is addressed to. Resolved at fan-out time:
 *   project — everyone who can view the project the notification names
 *   admins  — platform administrators only
 *   self    — exactly one person; never fanned out
 */
export type NotificationAudience = 'project' | 'admins' | 'self';

export interface CategoryMeta {
  group: NotificationGroup;
  label: string;
  description: string;
  audience: NotificationAudience;
  /** Defaults applied when a user has no row for this category. */
  defaults: ChannelSet & { minSeverity: NotificationSeverity };
  /** Addressed to one person about their own account: cannot be switched off. */
  mandatory?: boolean;
  /** Only offered to platform administrators; hidden from everyone else's matrix. */
  adminOnly?: boolean;
}

const set = (inapp: boolean, push: boolean, rocketchat: boolean, minSeverity: NotificationSeverity): ChannelSet & { minSeverity: NotificationSeverity } => ({
  inapp,
  push,
  rocketchat,
  minSeverity,
});

/**
 * In-app is on everywhere: it is a durable feed that costs nothing and is what makes the bell
 * useful on day one. Push is on only where being interrupted is the point, and gated at
 * `warning` so a green deploy does not buzz a phone. Rocket.Chat stays off until someone
 * links and verifies a username.
 */
export const CATEGORY_META: Record<NotificationCategory, CategoryMeta> = {
  deploy: {
    group: 'delivery',
    label: 'Deployments',
    description: 'Deploys, rollbacks, configuration applies and route changes finishing.',
    audience: 'project',
    defaults: set(true, true, false, 'warning'),
  },
  operations: {
    group: 'delivery',
    label: 'Operation failures',
    description: 'Any other operation that failed, was lost, or is stuck without an outcome.',
    audience: 'project',
    defaults: set(true, true, false, 'warning'),
  },
  approval: {
    group: 'delivery',
    label: 'Waiting for approval',
    description: 'Work paused until a person reviews it, such as a VPS enrollment report.',
    audience: 'admins',
    defaults: set(true, true, false, 'warning'),
    adminOnly: true,
  },
  health: {
    group: 'infrastructure',
    label: 'Application health',
    description: 'An application became unhealthy, stopped unexpectedly, or recovered.',
    audience: 'project',
    defaults: set(true, true, false, 'warning'),
  },
  agent: {
    group: 'infrastructure',
    label: 'VPS and agent connectivity',
    description: 'An agent stopped reporting, came back, or left work with an unknown outcome.',
    audience: 'admins',
    defaults: set(true, true, false, 'warning'),
    adminOnly: true,
  },
  routing: {
    group: 'infrastructure',
    label: 'Domains and certificates',
    description: 'DNS records that do not point here, and certificates that could not be issued.',
    audience: 'project',
    defaults: set(true, false, false, 'warning'),
  },
  config: {
    group: 'infrastructure',
    label: 'Pending configuration',
    description: 'Saved configuration differs from what the running services received.',
    audience: 'project',
    defaults: set(true, false, false, 'info'),
  },
  capacity: {
    group: 'infrastructure',
    label: 'Disk and capacity',
    description: 'A VPS running low on disk space.',
    audience: 'admins',
    defaults: set(true, true, false, 'warning'),
    adminOnly: true,
  },
  backup: {
    group: 'data',
    label: 'Backups',
    description: 'Backups verified and stored, or failed.',
    audience: 'project',
    defaults: set(true, true, false, 'warning'),
  },
  restore: {
    group: 'data',
    label: 'Restores',
    description: 'Restoring a backup over a live database, and how it ended.',
    audience: 'project',
    defaults: set(true, true, false, 'warning'),
  },
  migration: {
    group: 'data',
    label: 'Data migrations',
    description: 'Moving data between servers or environments, including steps that failed.',
    audience: 'project',
    defaults: set(true, true, false, 'warning'),
  },
  access: {
    group: 'security',
    label: 'Your access changed',
    description: 'Your sessions were revoked, or your access to a project was removed.',
    audience: 'self',
    defaults: set(true, true, false, 'info'),
    mandatory: true,
  },
  shell: {
    group: 'security',
    label: 'Privileged shell and SSH',
    description: 'Shell sessions opened on a VPS, and SSH certificates issued.',
    audience: 'admins',
    defaults: set(true, false, false, 'warning'),
    adminOnly: true,
  },
  secret: {
    group: 'security',
    label: 'Secret reveals',
    description: 'A stored secret value was shown to someone.',
    audience: 'project',
    defaults: set(true, false, false, 'warning'),
  },
  platform: {
    group: 'security',
    label: 'Platform settings',
    description: 'Platform settings changed, and VPS maintenance or deployment switches.',
    audience: 'admins',
    defaults: set(true, false, false, 'warning'),
    adminOnly: true,
  },
};

export const NOTIFICATION_KINDS = [
  // deploy
  'deploy.succeeded',
  'deploy.failed',
  'deploy.canceled',
  'rollback.succeeded',
  'rollback.failed',
  'apply-config.succeeded',
  'apply-config.failed',
  'reroute.succeeded',
  'reroute.failed',
  // operations
  'lifecycle.failed',
  'job.failed',
  'job.unknown',
  'job.lost',
  'job.stuck',
  'job.interrupted',
  'data.deleted',
  // approval
  'approval.enrollment',
  'approval.device',
  // health
  'target.unhealthy',
  'target.recovered',
  'target.stopped_unexpectedly',
  // agent
  'agent.disconnected',
  'agent.stale',
  'agent.reconnected',
  'vps.enrollment_failed',
  // routing
  'endpoint.dns_problem',
  'endpoint.dns_ok',
  'endpoint.cert_failed',
  'endpoint.cert_ok',
  // config
  'config.pending',
  // capacity
  'capacity.disk_warning',
  'capacity.disk_critical',
  'capacity.disk_ok',
  // backup
  'backup.verified',
  'backup.failed',
  // restore
  'restore.succeeded',
  'restore.failed',
  'restore.failed_mutated',
  // migration
  'migration.succeeded',
  'migration.failed',
  'migration.step_failed',
  'migration.stuck',
  'migration.artifacts_expiring',
  // access
  'access.revoked',
  // shell
  'shell.opened',
  'shell.terminated',
  'ssh.certificate_issued',
  // secret
  'secret.revealed',
  // platform
  'platform.settings_changed',
  'vps.maintenance_changed',
  'vps.deployments_disabled',
  'push.misconfigured',
  'webhook.disabled',
  'rocketchat.unavailable',
  'notification.test',
] as const;
export type NotificationKind = (typeof NOTIFICATION_KINDS)[number];

export interface KindMeta {
  category: NotificationCategory;
  severity: NotificationSeverity;
  /** A successful outcome: suppressed unless the category's severity floor allows `info`. */
  success?: boolean;
}

export const KIND_META: Record<NotificationKind, KindMeta> = {
  'deploy.succeeded': { category: 'deploy', severity: 'info', success: true },
  'deploy.failed': { category: 'deploy', severity: 'critical' },
  'deploy.canceled': { category: 'deploy', severity: 'info' },
  // A rollback always matters, even when it works: something was wrong enough to go back.
  'rollback.succeeded': { category: 'deploy', severity: 'warning' },
  'rollback.failed': { category: 'deploy', severity: 'critical' },
  'apply-config.succeeded': { category: 'deploy', severity: 'info', success: true },
  'apply-config.failed': { category: 'deploy', severity: 'warning' },
  'reroute.succeeded': { category: 'routing', severity: 'info', success: true },
  'reroute.failed': { category: 'routing', severity: 'warning' },

  'lifecycle.failed': { category: 'operations', severity: 'warning' },
  'job.failed': { category: 'operations', severity: 'warning' },
  'job.unknown': { category: 'operations', severity: 'warning' },
  'job.lost': { category: 'operations', severity: 'critical' },
  'job.stuck': { category: 'operations', severity: 'warning' },
  'job.interrupted': { category: 'operations', severity: 'warning' },
  // Deleting persistent data is irreversible: say so even though it succeeded.
  'data.deleted': { category: 'operations', severity: 'critical' },

  'approval.enrollment': { category: 'approval', severity: 'critical' },
  'approval.device': { category: 'approval', severity: 'warning' },

  'target.unhealthy': { category: 'health', severity: 'critical' },
  'target.recovered': { category: 'health', severity: 'info', success: true },
  'target.stopped_unexpectedly': { category: 'health', severity: 'critical' },

  'agent.disconnected': { category: 'agent', severity: 'critical' },
  'agent.stale': { category: 'agent', severity: 'warning' },
  'agent.reconnected': { category: 'agent', severity: 'info', success: true },
  'vps.enrollment_failed': { category: 'agent', severity: 'warning' },

  'endpoint.dns_problem': { category: 'routing', severity: 'warning' },
  'endpoint.dns_ok': { category: 'routing', severity: 'info', success: true },
  'endpoint.cert_failed': { category: 'routing', severity: 'warning' },
  'endpoint.cert_ok': { category: 'routing', severity: 'info', success: true },

  'config.pending': { category: 'config', severity: 'info' },

  'capacity.disk_warning': { category: 'capacity', severity: 'warning' },
  'capacity.disk_critical': { category: 'capacity', severity: 'critical' },
  'capacity.disk_ok': { category: 'capacity', severity: 'info', success: true },

  'backup.verified': { category: 'backup', severity: 'info', success: true },
  'backup.failed': { category: 'backup', severity: 'warning' },

  'restore.succeeded': { category: 'restore', severity: 'warning' },
  'restore.failed': { category: 'restore', severity: 'warning' },
  // The database may be partially modified and services were left stopped on purpose.
  'restore.failed_mutated': { category: 'restore', severity: 'critical' },

  'migration.succeeded': { category: 'migration', severity: 'info', success: true },
  'migration.failed': { category: 'migration', severity: 'critical' },
  'migration.step_failed': { category: 'migration', severity: 'warning' },
  'migration.stuck': { category: 'migration', severity: 'critical' },
  'migration.artifacts_expiring': { category: 'migration', severity: 'warning' },

  'access.revoked': { category: 'access', severity: 'critical' },

  'shell.opened': { category: 'shell', severity: 'info' },
  'shell.terminated': { category: 'shell', severity: 'warning' },
  'ssh.certificate_issued': { category: 'shell', severity: 'info' },

  'secret.revealed': { category: 'secret', severity: 'warning' },

  'platform.settings_changed': { category: 'platform', severity: 'warning' },
  'vps.maintenance_changed': { category: 'platform', severity: 'info' },
  'vps.deployments_disabled': { category: 'platform', severity: 'warning' },
  'push.misconfigured': { category: 'platform', severity: 'critical' },
  'webhook.disabled': { category: 'platform', severity: 'warning' },
  'rocketchat.unavailable': { category: 'platform', severity: 'critical' },
  'notification.test': { category: 'platform', severity: 'info', success: true },
};

const SEVERITY_ORDER: Record<NotificationSeverity, number> = { info: 0, warning: 1, critical: 2 };

/** True when `severity` is at least as loud as the floor a person set for the category. */
export function meetsSeverity(severity: NotificationSeverity, floor: NotificationSeverity): boolean {
  return SEVERITY_ORDER[severity] >= SEVERITY_ORDER[floor];
}

export const isCritical = (severity: NotificationSeverity): boolean => severity === 'critical';

/**
 * The one mapping from a finished job to a notification kind. Exhaustive over JOB_KINDS:
 * a new job kind will not compile until someone decides what it announces (`null` is a
 * deliberate "this one is not worth telling anybody about").
 *
 * `targetState` picks the honest restore outcome: a restore that failed after touching the
 * database is a different message from one that changed nothing.
 */
export function kindForJob(
  kind: JobKind,
  state: 'succeeded' | 'failed' | 'canceled',
  result?: { targetState?: string },
): NotificationKind | null {
  const ok = state === 'succeeded';
  switch (kind) {
    case 'deploy':
      return state === 'canceled' ? 'deploy.canceled' : ok ? 'deploy.succeeded' : 'deploy.failed';
    case 'rollback':
      return state === 'canceled' ? null : ok ? 'rollback.succeeded' : 'rollback.failed';
    case 'apply-config':
      return state === 'canceled' ? null : ok ? 'apply-config.succeeded' : 'apply-config.failed';
    case 'reroute':
      return state === 'canceled' ? null : ok ? 'reroute.succeeded' : 'reroute.failed';
    case 'lifecycle':
      // Starting and stopping on purpose is not news; failing to is.
      return ok || state === 'canceled' ? null : 'lifecycle.failed';
    case 'backup':
      return state === 'canceled' ? null : ok ? 'backup.verified' : 'backup.failed';
    case 'restore':
      if (state === 'canceled') return null;
      if (ok) return 'restore.succeeded';
      return result?.targetState === 'stopped-after-failed-restore' ? 'restore.failed_mutated' : 'restore.failed';
    case 'delete-data':
      return state === 'canceled' ? null : ok ? 'data.deleted' : 'job.failed';
    case 'migrate':
      return state === 'canceled' ? null : ok ? 'migration.succeeded' : 'migration.failed';
    // Migration children are covered by the parent's outcome; see the parent_job_id guard.
    case 'migrate-transfer':
    case 'migrate-cutover':
      return ok || state === 'canceled' ? null : 'migration.step_failed';
    case 'enroll':
      return ok || state === 'canceled' ? null : 'vps.enrollment_failed';
    case 'remove':
    case 'vps-remove':
    case 'agent-upgrade':
    case 'ssh-ca-install':
    case 'ssh-ca-remove':
      return ok || state === 'canceled' ? null : 'job.failed';
    default: {
      // Exhaustiveness guard: adding a JOB_KIND without a case fails the build here.
      const never: never = kind;
      return never;
    }
  }
}

/* ---------------------------------------------------------------------- DTOs */

export interface NotificationDTO {
  id: string;
  kind: NotificationKind;
  category: NotificationCategory;
  severity: NotificationSeverity;
  title: string;
  body?: string;
  /** Path within the administrator, never an absolute URL: the public URL can change. */
  href?: string;
  projectKey?: string;
  environment?: string;
  targetId?: string;
  vpsId?: string;
  jobId?: string;
  migrationId?: string;
  createdAt: string;
  readAt?: string;
}

export interface NotificationPageDTO {
  items: NotificationDTO[];
  unread: number;
  /** Pass as `before` to fetch the next page; absent when the list is exhausted. */
  nextCursor?: string;
}

export interface CategoryPrefDTO {
  category: NotificationCategory;
  group: NotificationGroup;
  label: string;
  description: string;
  inapp: boolean;
  push: boolean;
  rocketchat: boolean;
  minSeverity: NotificationSeverity;
  /** Cannot be switched off; rendered as a locked row so it does not look forgotten. */
  mandatory: boolean;
}

export interface MuteDTO {
  projectKey: string;
  /** Absent means indefinite: until it is turned back on. */
  until?: string;
  createdAt: string;
}

export interface NotificationPreferencesDTO {
  categories: CategoryPrefDTO[];
  mutes: MuteDTO[];
}

/** The mute durations offered by the silence button, in minutes. `null` is indefinite. */
export const MUTE_DURATIONS: (number | null)[] = [15, 60, 480, null];

export function muteLabel(minutes: number | null): string {
  if (minutes === null) return 'Until I turn it back on';
  if (minutes < 60) return `${minutes} minutes`;
  return minutes === 60 ? '1 hour' : `${minutes / 60} hours`;
}
